import { useScrollReveal } from '../hooks/useScrollReveal';
import { useParallax } from '../hooks/useParallax';

export default function P13UnifiedRubric({ forceVisible = false }: { forceVisible?: boolean }) {
  const { ref: titleRef, isVisible: titleVisible } = useScrollReveal(0.12, forceVisible);
  const { ref: orb1Ref, offset: orb1Y } = useParallax(0.05);
  const { ref: orb2Ref, offset: orb2Y } = useParallax(-0.04);

  return (
    <div className="relative min-h-screen flex flex-col justify-center overflow-hidden bg-background-50">
      <div ref={orb1Ref} className="absolute top-20 right-24 w-80 h-80 rounded-full bg-primary-100/10 blur-[100px] pointer-events-none" style={{ transform: `translateY(${orb1Y}px)` }} />
      <div ref={orb2Ref} className="absolute bottom-20 left-16 w-96 h-96 rounded-full bg-accent-100/8 blur-[120px] pointer-events-none" style={{ transform: `translateY(${orb2Y}px)` }} />

      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 py-12 md:py-16">
        <div ref={titleRef} className="text-center mb-8 md:mb-10">
          <span className={`inline-block text-xs font-body font-semibold text-accent-600 uppercase tracking-[0.15em] mb-4 transition-all duration-700 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            评价标准化
          </span>
          <h2 className={`font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 mb-4 transition-all duration-1000 delay-100 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            统一评价表<span className="text-primary-600">为什么必要</span>
          </h2>
          <p className={`text-base text-foreground-600 font-body max-w-2xl mx-auto leading-relaxed transition-all duration-1000 delay-200 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            把专家长期形成的判断转成可执行、可训练、可比较的标准
          </p>
        </div>

        {/* Three-stage flow */}
        <div className={`flex flex-col md:flex-row items-center justify-center gap-6 md:gap-8 mb-10 transition-all duration-1000 delay-300 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          {/* Stage 1 */}
          <div className="glass-liquid border-accent-200/30 rounded-2xl p-6 md:p-8 text-center w-full md:w-64">
            <div className="relative z-10">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl glass-gold border-accent-200/40 flex items-center justify-center">
                <i className="ri-user-star-line text-3xl text-accent-600" />
              </div>
              <h3 className="font-heading text-lg font-bold text-foreground-800 mb-2">专家经验</h3>
              <p className="text-xs text-foreground-500 font-body">长期形成的专业判断与教学直觉</p>
            </div>
          </div>

          {/* Arrow */}
          <div className="flex-shrink-0">
            <div className="w-10 h-10 rounded-full glass border-white/60 flex items-center justify-center">
              <i className="ri-arrow-right-line text-primary-500 text-lg" />
            </div>
          </div>

          {/* Stage 2 */}
          <div className="glass-liquid border-primary-200/30 rounded-2xl p-6 md:p-8 text-center w-full md:w-72 relative">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-accent-100 border border-accent-200/40">
              <span className="text-xs font-body font-semibold text-accent-600">核心环节</span>
            </div>
            <div className="relative z-10 mt-3">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl glass-blue border-primary-200/40 flex items-center justify-center">
                <i className="ri-file-list-3-line text-3xl text-primary-600" />
              </div>
              <h3 className="font-heading text-lg font-bold text-foreground-800 mb-2">统一评价表</h3>
              <p className="text-xs text-foreground-500 font-body mb-4">把专家判断转化为结构化标准</p>
              <div className="flex flex-wrap justify-center gap-1.5">
                {['五类', '19项', '→', '24项指标'].map((tag) => (
                  <span key={tag} className="px-2.5 py-1 rounded-lg glass-gold border-accent-200/30 text-xs font-mono text-accent-700">{tag}</span>
                ))}
              </div>
            </div>
          </div>

          {/* Arrow */}
          <div className="flex-shrink-0">
            <div className="w-10 h-10 rounded-full glass border-white/60 flex items-center justify-center">
              <i className="ri-arrow-right-line text-primary-500 text-lg" />
            </div>
          </div>

          {/* Stage 3 */}
          <div className="glass-liquid border-primary-200/30 rounded-2xl p-6 md:p-8 text-center w-full md:w-64">
            <div className="relative z-10">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl glass-blue border-primary-200/40 flex items-center justify-center">
                <i className="ri-brain-line text-3xl text-primary-600" />
              </div>
              <h3 className="font-heading text-lg font-bold text-foreground-800 mb-2">专用评价模型学习</h3>
              <p className="text-xs text-foreground-500 font-body">让AI能学、能评、能验证</p>
            </div>
          </div>
        </div>

        <div className={`max-w-2xl mx-auto transition-all duration-1000 delay-700 ${titleVisible ? 'opacity-100' : 'opacity-0'}`}>
          <div className="p-5 rounded-2xl glass-strong border-primary-200/30 text-center">
            <p className="text-sm text-foreground-600 font-body leading-relaxed">
              统一评价表的作用，<strong className="text-primary-700">不是削弱专家</strong>，而是把专家经验沉淀下来，让AI能学、能评、能验证。同一个标准，不同专家用同一把尺子衡量，模型才能在统一框架里训练。
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}